'use client';
import { FC, useMemo } from 'react';
import Link from 'next/link';
import { StaticImageData } from 'next/image';
import { twMerge } from 'tailwind-merge';

import { Button, Heading, NextImage, Svg, Text } from '@/components';
import ShareIconButton from '@/components/Button/Share';
import HeartIconButton from '@/components/Button/Heart';
import { russolo } from '@/config/fonts';
import { useWishlist } from '@/context/wishlistContext';
import { CloseIcon } from '@/icons';
import { checkItemInWishlist, pushGA4Event } from '@/utils/helper';
import { usePathname, useRouter } from 'next/navigation';
import { CustomAttribute } from '@/models/user-details';
import { RECIPE_CARD_TAGS } from '@/constants';
import { useCleverTap } from '@/context/clevertapContext';

type ReciepeCardPropTypes = {
  src: StaticImageData | string;
  sku: string;
  heading: string;
  name?: string;
  duration?: string;
  author?: string;
  heartBtn?: boolean;
  removeBtn?: boolean;
  tags?: CustomAttribute[];
  plpCard?: boolean;
  classNames?: {
    root?: string;
  };
  eventMetadata?: {
    sectionName?: string;
    rank?: number;
  };
  searchResult?: 'DIRECT' | 'AUTO_COMPLETE';
  onRemove?: (e: any) => void;
};

const TAG_COLOR = {
  DIETARY_TAGS: RECIPE_CARD_TAGS[0],
  SKILL_LEVEL_TAGS: RECIPE_CARD_TAGS[1],
};

const RecipeCard: FC<ReciepeCardPropTypes> = ({
  name = '',
  src,
  sku,
  duration,
  author,
  heading,
  heartBtn = true,
  removeBtn = false,
  tags,
  plpCard,
  classNames,
  eventMetadata,
  searchResult,
  onRemove,
}) => {
  const router = useRouter();
  const pathname = usePathname();
  const { pushEvent } = useCleverTap();
  const { addToWishlist, removeFromWishlist, wishlistItems } = useWishlist();

  const isWishlisted = useMemo(
    () => checkItemInWishlist(wishlistItems, sku || ''),
    [wishlistItems]
  );

  const toggleWishlist = () => {
    const searchQuery = (
      document.getElementById('searchBar') as HTMLInputElement
    )?.value;
    if (isWishlisted) {
      removeFromWishlist({ name, sku });
    } else {
      pushEvent({
        event: 'Search Result Wishlist',
        data: {
          'Search Keyword': searchQuery,
          'Search Type': searchResult,
          'Product SKU': sku,
          'Product Name': name,
          'Product Type': 'Recipe',
        },
      });
      addToWishlist({ name, sku });
    }
  };

  function handleArticleClick() {
    const dataToPush: any = {
      'Page Name': window.document.title,
      'Page Url': pathname,
      SKU: sku,
      Type: 'Recipe',
    };
    const searchQuery = (
      document.getElementById('searchBar') as HTMLInputElement
    )?.value;
    if (eventMetadata?.sectionName) {
      dataToPush['Section Name'] = eventMetadata?.sectionName;
      dataToPush['Rank'] = eventMetadata?.rank;
    }
    pushEvent({
      event: 'Article Clicked',
      data: dataToPush,
    });
    pushEvent({
      event: 'content_card_clicked',
      data: { content_id: sku },
    });
    pushEvent({
      event: 'Search Result Clicked',
      data: {
        'Search Keyword': searchQuery,
        'Search Type': searchResult,
        'Product SKU': sku,
        'Product Name': name,
        'Product Type': 'Recipe',
      },
    });

    pushGA4Event('content_card_clicked', {
      category: 'Content',
      content_id: sku,
      type: 'Recipe',
    });
  }

  tags = tags?.filter(
    (tag, index, self) =>
      index ===
      self.findIndex(
        t => t.attribute_code === tag.attribute_code && t.value === tag.value
      )
  );

  return (
    <div
      className={twMerge(
        'group col-span-1 row-span-1 mx-auto block w-full max-w-76.5 flex-1 space-y-2',
        plpCard ? 'max-sm:max-w-45.5' : '',
        classNames?.root
      )}>
      <div
        className={twMerge(
          'aspect-102/173 h-full w-full max-w-76.5 cursor-pointer space-y-4 rounded-md bg-[#f6f6f6] p-4',
          plpCard
            ? 'max-sm:aspect-125/224 max-sm:max-w-45.5 max-sm:space-y-2 max-sm:p-2'
            : ''
        )}
        onClick={() => {
          handleArticleClick();
          router.push(`/recipes/${sku}`);
        }}>
        <div className='relative aspect-83/94 w-full rounded-md sm:aspect-137/155'>
          <NextImage
            fill
            src={src}
            alt={heading}
            className='object-cover'
            sizes='(max-width: 768px) 60vw, 33vw'
          />
          {tags && tags.length > 0 && (
            <ul
              className={twMerge(
                'absolute left-4 top-4 flex flex-col gap-2 rounded-sm',
                plpCard && 'max-md:left-2 max-md:top-2'
              )}>
              {tags
                .sort((a, b) => {
                  if (a.attribute_code === TAG_COLOR.SKILL_LEVEL_TAGS) {
                    return 1; // Place skill level tags below other tags
                  } else if (b.attribute_code === TAG_COLOR.SKILL_LEVEL_TAGS) {
                    return -1; // Place other tags above skill level tags
                  } else {
                    return 0; // Preserve the order for other tags
                  }
                })
                .filter(item => item.value !== '')
                .map(tag => (
                  <li
                    key={tag.attribute_code}
                    className={twMerge(
                      'w-fit rounded-sm px-2 py-1',
                      tag.attribute_code === TAG_COLOR.SKILL_LEVEL_TAGS
                        ? 'bg-[#D48A7A]'
                        : 'bg-[#38383A]'
                    )}>
                    <p
                      className={twMerge(
                        'font-gilroy line-clamp-1 text-white',
                        plpCard ? 'max-md:text-xs' : 'text-base'
                      )}>
                      {tag.value}
                    </p>
                  </li>
                ))}
            </ul>
          )}
          <div
            className={twMerge(
              'absolute right-0 top-0 p-2',
              plpCard ? 'max-sm:right-2 max-sm:top-[0.3rem] max-sm:p-0' : ''
            )}>
            <div
              className={twMerge(
                'flex flex-col gap-3',
                plpCard ? 'max-sm:gap-[0.3rem]' : ''
              )}>
              {removeBtn && onRemove && (
                <button
                  className='rounded-full bg-white p-[0.4rem] md:p-2'
                  onClick={e => onRemove(e)}>
                  <Svg className='h-[0.65rem] w-[0.65rem] stroke-fig md:h-4 md:w-4'>
                    <CloseIcon />
                  </Svg>
                </button>
              )}
              {heartBtn && (
                <HeartIconButton
                  isWishlisted={isWishlisted}
                  onClick={toggleWishlist}
                  classNames={
                    plpCard
                      ? {
                          btn: 'max-sm:h-6 max-sm:w-6 max-sm:p-0',
                          svg: 'max-sm:w-[0.825rem] max-sm:h-[0.825rem] max-sm:mx-auto',
                        }
                      : {}
                  }
                />
              )}
              <ShareIconButton
                details={{
                  link: `/recipes/${sku}`,
                  title: name,
                  type: 'recipe',
                }}
                classNames={
                  plpCard
                    ? {
                        btn: 'max-sm:h-6 max-sm:w-6 max-sm:p-0',
                        svg: 'max-sm:w-[0.825rem] max-sm:h-[0.825rem] max-sm:mx-auto',
                      }
                    : {}
                }
              />
            </div>
          </div>
        </div>

        <div
          aria-label='info'
          className={twMerge(
            'h-36 text-center',
            plpCard ? 'space-y-3 max-sm:h-24' : 'space-y-4'
          )}>
          <Heading
            className={twMerge(
              `line-clamp-2 p-0 font-semibold`,
              russolo?.className,
              plpCard ? 'text-sm sm:text-2xl' : 'text-2xl'
            )}>
            {heading}
          </Heading>
          <div className='space-y-3'>
            <Text
              className={twMerge(
                'line-clamp-1 font-semibold sm:line-clamp-2 sm:text-base md:line-clamp-3',
                plpCard ? 'text-xs' : 'text-base'
              )}>
              {author}
            </Text>
            <Text
              className={twMerge(
                'line-clamp-1 font-semibold text-black/50 sm:text-base md:line-clamp-3',
                plpCard ? 'text-xs' : 'text-base'
              )}>
              Cook Time: {duration}
            </Text>
          </div>
        </div>
      </div>

      <div>
        <Link href={`/recipes/${sku}`} prefetch={false}>
          <Button
            label='Read Recipe'
            className={twMerge(
              'py-[0.9rem] group-hover:visible lg:invisible',
              plpCard ? 'max-sm:py-2 ' : ''
            )}
          />
        </Link>
      </div>
    </div>
  );
};

export default RecipeCard;
